import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import '../styles/Book.css';
import { FaEye, FaHeart } from 'react-icons/fa';
import { MdOutlineMenuBook } from 'react-icons/md';
import getBookCoverUrl from '../utils/getBookCoverUrl';

const Book = () => {
  const [bookPageDetails, setBookPageDetails] = useState(null);
  const [bookPageChapters, setBookPageChapters] = useState([]);
  const [bookPageIsInLibrary, setBookPageIsInLibrary] = useState(false);
  const [bookPageRecommendedBooks, setBookPageRecommendedBooks] = useState([]);
  const { bookId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchBookPageDetails = async () => {
      try {
        const response = await api.get(`/books/${bookId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const bookData = response.data.book;

        if (!bookData.cover_image_url) {
          console.error('Cover image is required. Please upload an image.');
          return;
        }

        setBookPageDetails({
          ...bookData,
          cover_image_url: getBookCoverUrl(bookData.cover_image_url)
        });

        const sortedBookPageChapters = (response.data.chapters || []).sort((a, b) => a.chapter_number - b.chapter_number);
        setBookPageChapters(sortedBookPageChapters);

        setBookPageIsInLibrary(bookData.isInLibrary);

        const recommendedResponse = await api.get(`/books/${bookId}/recommendations`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const recommendedBookPagesUpdated = recommendedResponse.data.map(book => ({
          ...book,
          cover_image_url: getBookCoverUrl(book.cover_image_url)
        }));
        setBookPageRecommendedBooks(recommendedBookPagesUpdated);
      } catch (error) {
        console.error('Error fetching book details:', error);
      }
    };

    fetchBookPageDetails();
  }, [bookId, navigate]);

  const toggleBookPageLibrary = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await api.post(`/books/${bookId}/toggle-library`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setBookPageIsInLibrary(response.data.isInLibrary);
    } catch (error) {
      console.error('Error toggling library status:', error);
    }
  };

  const startBookPageReading = () => {
    if (bookPageChapters.length > 0) {
      const firstChapter = bookPageChapters.reduce((min, chapter) =>
        chapter.chapter_number < min.chapter_number ? chapter : min
      );
      navigate(`/read/${bookId}/${firstChapter.chapter_id}`);
    }
  };

  const navigateToBookPageAuthorProfile = () => {
    navigate(`/profile/${bookPageDetails.author_id}`);
  };

  // Function to format description with proper paragraphs
  const formatDescription = (description) => {
    if (!description) return 'Açıklama henüz eklenmedi.';
    
    // Split by double line breaks first, then single line breaks
    const paragraphs = description
      .split(/\n\s*\n/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0);
    
    if (paragraphs.length <= 1) {
      // If no double line breaks, split by single line breaks
      return description
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
    
    return paragraphs;
  };

  if (!bookPageDetails) return <div className="loading">Loading...</div>;

  const formattedDescription = formatDescription(bookPageDetails.description);

  return (
    <div className="bookPage-details">
      <div className="bookPage-header-full-width">
        <div className="bookPage-header-content">
          <div className="bookPage-cover-container">
            <img
              src={bookPageDetails.cover_image_url}
              alt={bookPageDetails.title}
              className="bookPage-cover"
              onError={(e) => {
                console.error(`Error loading image: ${e.target.src}`);
              }}
            />
          </div>
          <div className="bookPage-info">
            <h1>{bookPageDetails.title || 'Kitap Başlığı'}</h1>
            <p className="bookPage-author" onClick={navigateToBookPageAuthorProfile}>
              {bookPageDetails.username || 'Yazar Adı'}
            </p>
            <div className="bookPage-stats">
              <span><FaEye /> {bookPageDetails.total_views || 0}</span>
              <span><FaHeart /> {bookPageDetails.total_likes || 0}</span>
              <span><MdOutlineMenuBook /> {bookPageChapters.length || 0}</span>
            </div>
            <div className="bookPage-button-group">
              <button className="bookPage-read-button" onClick={startBookPageReading}>
                Okumaya başla
              </button>
              <button className="bookPage-library-button" onClick={toggleBookPageLibrary}>
                {bookPageIsInLibrary ? 'Kütüphaneden Çıkar' : 'Kütüphaneye Ekle'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bookPage-content-container">
        <div className="bookPage-content-left">
          <div className="bookPage-description">
            <h2>Açıklama</h2>
            <div className="bookPage-description-content">
              {Array.isArray(formattedDescription) ? (
                formattedDescription.map((paragraph, index) => (
                  <p key={index} className="bookPage-paragraph">
                    {paragraph}
                  </p>
                ))
              ) : (
                <p className="bookPage-paragraph">{formattedDescription}</p>
              )}
            </div>
          </div>
          
          <div className="bookPage-chapters">
            <h2>Kitap İçerik Listesi</h2>
            <div className="bookPage-chapters-container">
              {bookPageChapters.length > 0 ? (
                <ul>
                  {bookPageChapters.map((chapter, index) => (
                    <li key={chapter.chapter_id} className="bookPage-chapter-item">
                      <div className="bookPage-chapter-number">
                        {index + 1}
                      </div>
                      <div className="bookPage-chapter-content">
                        <Link to={`/read/${bookId}/${chapter.chapter_id}`} className="bookPage-chapter-link">
                          {chapter.title}
                        </Link>
                        <span className="bookPage-chapter-date">
                          {new Date(chapter.created_at).toLocaleDateString('tr-TR', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="bookPage-no-chapters">Bu kitabın bölümleri henüz eklenmedi.</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="bookPage-content-right">
          <h2>Bunları da beğenebilirsiniz</h2>
          <div className="bookPage-recommendations">
            {bookPageRecommendedBooks.length > 0 ? (
              bookPageRecommendedBooks.map((recBook) => (
                <Link to={`/book/${recBook.book_id}`} key={recBook.book_id} className="bookPage-recommended-book">
                  <div className="bookPage-recommended-cover">
                    <img
                      src={recBook.cover_image_url}
                      alt={recBook.title}
                      className="bookPage-recommended-image"
                      onError={(e) => {
                        console.error(`Error loading image: ${e.target.src}`);
                      }}
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                  <div className="bookPage-recommended-book-info">
                    <h3>{recBook.title}</h3>
                    <div className="bookPage-stats">
                      <span><FaEye /> {recBook.total_views}</span>
                      <span><FaHeart /> {recBook.total_likes}</span>
                      <span><MdOutlineMenuBook /> {recBook.chapter_count}</span>
                    </div>
                    <p>{recBook.description?.substring(0, 100)}...</p>
                  </div>
                </Link>
              ))
            ) : (
              <p className="bookPage-no-recommendations">Önerilen kitap yok.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Book;